import React, {FC, memo, useMemo} from 'react'
import {useQuery} from 'react-query'
import {useParams} from 'react-router-dom'
import {Post, RootState} from '../../../../setup'
import {UserInterface, UserWorkspace} from '../../../modules/auth/models/UserModel'
import {shallowEqual, useSelector} from 'react-redux'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {SAVAttachment, SAVResponsesTicket, useSAV} from '../../../@types'

const MESSAGE_TICKET = process.env.REACT_APP_SAV_TICKETS_MESSAGE_TICKET || 'sav/get-message'
const VALID_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg'] as const
const VALID_DOC_EXTENSIONS = ['pdf', 'docx', 'csv'] as const

// Types
type FileExtension = typeof VALID_IMAGE_EXTENSIONS[number] | typeof VALID_DOC_EXTENSIONS[number]

// Memoized components
const UserAvatar = memo(({initials}: {initials: string}) => (
    <div className="symbol-label fs-1 fw-bold bg-light-success text-success">
        {initials}
    </div>
))

const WorkspaceAvatar = memo(({icon}: {icon?: string}) => (
    <img src={icon}
         className="symbol symbol-45px"
         style={{objectFit: 'contain'}}
         alt="workspace icon"
    />
))

const Attachment = memo(({attachment}: {attachment: SAVAttachment}) => {
    const fileExtension = attachment.type.split('.').pop()?.toLowerCase() as FileExtension

    if (VALID_IMAGE_EXTENSIONS.includes(fileExtension as typeof VALID_IMAGE_EXTENSIONS[number])) {
        return (
            <img src={attachment.src}
                 className="rounded w-50px h-50px me-2 mb-2 border border-2 border-dashed"
                 style={{objectFit: 'contain'}}
                 alt={attachment.name}
                 loading="lazy"
            />
        )
    }

    if (VALID_DOC_EXTENSIONS.includes(fileExtension as typeof VALID_DOC_EXTENSIONS[number])) {
        return (
            <a
                href={attachment.src}
                target="_blank"
                rel="noopener noreferrer"
                download={attachment.name}
            >
                <img src={toAbsoluteUrl(`/media/svg/files/${fileExtension}.svg`)}
                     className="rounded w-50px h-50px me-2 mb-2"
                     alt={attachment.name}
                     loading="lazy"
                />
            </a>
        )
    }

    return null
})

const MessageCard = memo(({
                              response,
                              userInfo,
                          }: {
    response: SAVResponsesTicket
    userInfo: {
        initials: string
        firstname: string
        lastname: string
        workspace: UserWorkspace | null
    }
}) => {
    const isMessage = response.type === 'message'
    const {initials, firstname, lastname, workspace} = userInfo

    return (
        <div className="card card-bordered w-100">
            <div className="card-body p-5">
                <div className="w-100 d-flex flex-stack mb-2">
                    <div className="d-flex align-items-center">
                        <div className="symbol symbol-50px me-5 border border-2 border-dashed">
                            {isMessage ? (
                                <UserAvatar initials={initials} />
                            ) : (
                                <WorkspaceAvatar icon={workspace?.icon} />
                            )}
                        </div>
                        <div className="d-flex flex-column fw-semibold fs-5 text-gray-600 text-gray-900">
                            <div className="d-flex align-items-center">
                                <span className="text-gray-800 fw-bold fs-5 me-3">
                                    {isMessage
                                        ? `${firstname} ${lastname}`
                                        : workspace?.name || ''}
                                </span>
                            </div>
                            <span className="text-muted fw-semibold fs-6">
                                {response.created_at}
                            </span>
                        </div>
                    </div>
                </div>
                <div
                    className="fw-normal fs-5 text-gray-700 mb-3"
                    dangerouslySetInnerHTML={{__html: response.message}}
                />
                <div className="d-flex flex-wrap">
                    {response.attachments?.map((attachment, index) => (
                        <Attachment key={`${attachment.name}-${index}`} attachment={attachment} />
                    ))}
                </div>
            </div>
        </div>
    )
})


const Body: FC = () => {
    const {id} = useParams<{id: string}>()
    const {firstname, lastname, workspace}: UserInterface = useSelector<RootState>(
        ({auth}) => auth.user, shallowEqual,
    ) as UserInterface

    const {reloadKey} = useSAV()

    // Optimized query with error handling and loading states
    const {data: messages = [], isLoading, error} = useQuery(
        [`TICKET_RESPONSE_${id}`, id, reloadKey],
        async () => {
            const formData = new FormData()
            formData.append('sav_ticket_id', id)
            const response = await Post<{messages: SAVResponsesTicket[]}>(MESSAGE_TICKET, formData)
            return response.messages
        },
        {
            keepPreviousData: true,
            staleTime: 30000, // Consider data fresh for 30 seconds
            cacheTime: 300000, // Keep in cache for 5 minutes
            retry: 2, // Retry failed requests twice
            refetchOnWindowFocus: false, // Don't refetch when window regains focus
        },
    )

    const userInfo = useMemo(() => ({
        initials: `${firstname[0]}${lastname[0]}`,
        firstname,
        lastname,
        workspace,
    }), [firstname, lastname, workspace])

    // Handle loading and error states
    if (isLoading) return <div className="text-center p-5">Loading messages...</div>
    if (error) return <div className="text-center p-5 text-danger">Error loading messages</div>

    return (
        <div className="messages-container">
            {messages.map((response, index) => (
                <div className="mb-10" key={`${response.created_at}-${index}`}>
                    <MessageCard
                        response={response}
                        userInfo={userInfo}
                    />
                </div>
            ))}
        </div>
    )
}

// export {Body}
export default memo(Body)

